import React, { useCallback, useEffect, useRef, useState } from 'react';

import CodeIcon from '@mui/icons-material/Code';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  ClickAwayListener,
  Fade,
  Popper,
  TextareaAutosize,
  Chip,
  CardActionArea,
} from '@mui/material';

import * as _ from 'lodash';

import { alpha, useTheme } from '@mui/material/styles';

import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
  Card,
  CircularProgress,
  Container,
  List,
} from 'common/components/material';

import { CardContent } from 'common/components/material/Card';
import { ListItem } from '@mui/material';
import { ListItemText } from 'common/components/material/ListItemButton';

import { CXChip } from 'common/components/cx-chip';
import { EllipsisText } from 'common/components/ellipsis-text/ellipsis-text.component';
import { LinkTileWithAvatar } from 'common/components/link-tile-with-avatar/link-tile-with-avatar.component';

import { useGetChatResponse, useGetChatHistory } from '../hooks/useChatHooks';

import { generatePath } from 'react-router-dom';

import { ProfileIcon } from 'common/components/image';

import { useRouter, useSession } from 'common/hooks';

import AppRoutesEnum from 'common/routes/AppRoutes.enum';

import moment from 'moment';

import { useSearchableEntities } from '../hooks/useSearchableEntities';

// =============================================================================
// CONTRACTS
// =============================================================================

export type ChatIntentType =
  | 'LIST_SUMMARY'
  | 'PEOPLE_STATS_VIEW'
  | 'PROFILE_OVERVIEW'
  | 'MATCH_EXPLANATION'
  | 'PEOPLE_COMPARISON'
  | 'ACTIVITY_LIST';

export type ChatPresentation =
  | 'PEOPLE_MATCH'
  | 'TABLE'
  | 'RANKED_LIST'
  | 'TREND'
  | 'KPI'
  | 'COMPARISON'
  | 'EXPLANATION'
  | 'ACTIVITY_LIST';

export interface FilterItem {
  id: string;
  name: string;
}

export type ConstraintStrength = 'MUST' | 'SHOULD' | 'EXCLUDE';

export interface ActiveEntityFilter extends FilterItem {
  type?: string;
  strength: ConstraintStrength;
}

export interface ActiveFilters {
  required?: ActiveEntityFilter[];

  preferred?: ActiveEntityFilter[];

  excluded?: ActiveEntityFilter[];

  skill?: FilterItem[];

  location?: Array<
    FilterItem & {
      type: string;
    }
  >;

  orgUnit?: Array<
    FilterItem & {
      type: string;
    }
  >;

  focused_people?: FilterItem[];

  reports_to?: FilterItem[];

  include_indirect_reports?: boolean;

  tenure?: {
    min?: number;
    max?: number;
  };

  performance?: 'TOP' | 'BOTTOM';

  performance_ranks?: FilterItem[];

  activity_date?: {
    start?: string;
    end?: string;
  };

  activity_criticality?: FilterItem[];

  activity_type?: FilterItem[];

  activity_initiative?: FilterItem[];

  activity_source?: FilterItem[];
}

export interface TaggedEntity {
  id: string;
  label: string;
  entityType: string;
  clarificationFor?: string;
}

export interface CandidatePreview {
  profileId: string;
  name: string;

  latestEvidenceDate?: string;

  department?: string | null;
  division?: string | null;
  city?: string | null;
  country?: string | null;

  tenureYears?: number | null;

  manager?: string | null;

  performanceLabel?: string | null;

  evidenceCount?: number;

  matchedEntityCount?: number;

  matchedEntityIds?: string[];

  topSkills?: string[];

  matchReason?: string | null;
}

export interface SingleProfileData {
  profileId: string;
  name: string;
  location: string;
  department: string | null;
  division: string | null;
  tenureYears: number | null;
  manager: string | null;
  topSkills?: string[];
  skillGroups?: Array<{ label: string; count: number; skills: string[] }>;
  matchReason: string | null;
}

export interface AnalyticsResultData {
  columns: string[];
  rows: Record<string, any>[];

  truncated?: boolean;

  strategy?: string;

  totalCount?: number;
}

export interface ActivityPreview {
  id: string;

  personId: string;

  person: string;

  activity: string;

  type?: string | null;

  date?: string | null;
}

export interface NavigationMetadata {
  directToClient?: boolean;

  presentation?: ChatPresentation;

  intent?: ChatIntentType;

  title?: string;

  button_label?: string;

  total_count?: number;

  active_filters?: ActiveFilters;

  unresolvedTerms?: string[];

  ignoredTerms?: string[];

  resolvedEntities?: Array<{ id: string; name: string; type: string }>;

  progressMessages?: string[];

  data?: {
    candidates?: CandidatePreview[];

    activities?: ActivityPreview[];

    result?: AnalyticsResultData;

    profile?: SingleProfileData;

    person?: {
      id: string;
      name: string;
      summary?: string;
      required?: string[];
      preferred?: string[];
    };

    people?: Array<{
      id: string;
      name: string;
      [key: string]: any;
    }>;

    comparison?: any;

    /**
     * Attribute labels for a comparison, used both as the row headings and as
     * the keys the values are read from on each person.
     */
    columns?: string[];

    id?: string;

    name?: string;

    location?: string | null;

    tenure?: string | null;

    manager?: string | null;

    [key: string]: any;
  };

  requiresClarification?: boolean;

  clarificationMessage?: string;

  clarificationOptions?: ClarificationOption[];

  /** A short note explaining an access restriction, rendered distinctly from the main response text. */
  accessNote?: string;
}

export interface ClarificationOption {
  id: string;
  label: string;
  entityType?: string;
  entity?: {
    id: string;
    name: string;
    completeName?: string;
    type: string;
    photo?: string;
  };
}

export interface ChatResponse {
  text: string;
}

export type Chat = {
  type: 'user' | 'assistant';

  response: ChatResponse;

  metadata?: NavigationMetadata;

  dateTime: Date;

  id?: string;

  taggedEntities?: TaggedEntity[];
};

interface MessageBubbleInput {
  rawUserInput: string;
  taggedEntities: TaggedEntity[];
}

// =============================================================================
// SHARED UI HELPERS
// =============================================================================

const scrollbarStyles = (palette: any) => ({
  scrollbarWidth: 'thin',

  scrollbarColor: 'transparent transparent',

  '&:hover': {
    scrollbarColor: `${alpha(palette.text.secondary, 0.3)} transparent`,
  },

  '&::-webkit-scrollbar': {
    width: '2px',
    height: '2px',
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'transparent',
    borderRadius: '2px',
  },

  '&:hover::-webkit-scrollbar-thumb': {
    backgroundColor: alpha(palette.text.secondary, 0.2),
  },
});

// =============================================================================
// FILTER CHIPS
// =============================================================================

/**
 * A profile answer is a direct lookup, not the product of a filter set, so
 * showing the running search scope underneath it describes a different
 * question than the one on screen.
 */
const isFilteredSearchIntent = (intent?: ChatIntentType) =>
  intent === 'LIST_SUMMARY' || intent === 'ACTIVITY_LIST' || intent === 'PEOPLE_STATS_VIEW';

export const RenderFilterChips: React.FC<{
  filters?: ActiveFilters;
}> = ({ filters }) => {
  const { palette } = useTheme<any>();

  if (!filters || Object.keys(filters).length === 0) {
    return null;
  }

  const hasVisibleFilters =
    !!filters.reports_to?.length ||
    !!filters.required?.length ||
    !!filters.preferred?.length ||
    !!filters.excluded?.length ||
    !!filters.tenure ||
    !!filters.performance ||
    !!filters.activity_date;

  if (!hasVisibleFilters) {
    return null;
  }

  const formatTenure = () => {
    const min = filters.tenure?.min;
    const max = filters.tenure?.max;

    if (min !== undefined && max !== undefined) {
      return `${min}–${max} yrs`;
    }

    if (min !== undefined) {
      return `${min}+ yrs`;
    }

    return `Up to ${max} yrs`;
  };

  const chipStyles = {
    maxWidth: '100%',
    height: 24,
    borderRadius: 1,
    backgroundColor: alpha(palette.Icons.Primary.IconFill, 0.04),
    border: `1px solid ${alpha(palette.Divider, 0.7)}`,
    color: palette.Text.Paragraph,
    fontSize: '0.68rem',
    '& .MuiChip-label': {
      display: 'block',
      minWidth: 0,
      overflow: 'hidden',
      px: 1,
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const renderEntityGroup = (label: string, entities: FilterItem[] | undefined) => {
    if (!entities?.length) {
      return null;
    }

    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',

            fontSize: '0.62rem',

            textTransform: 'uppercase',

            letterSpacing: 0.5,

            color: palette.Text.Caption,

            mb: 0.4,
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            display: 'flex',

            flexWrap: 'wrap',

            gap: 0.5,

            minWidth: 0,

            width: '100%',
          }}
        >
          {entities.map((entity) => (
            <Chip
              key={entity.id}
              label={entity.name}
              size="small"
              title={entity.name}
              sx={chipStyles}
            />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1,
        minWidth: 0,
        width: '100%',
        mt: 1.25,
        pt: 1.25,
        borderTop: `1px solid ${alpha(palette.Divider, 0.65)}`,
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" sx={{ color: palette.Text.Caption, fontWeight: 600 }}>
        Search criteria
      </Typography>

      {renderEntityGroup(
        'Required',
        filters.required?.filter(
          (entity) => !filters.reports_to?.some((manager) => manager.id === entity.id),
        ),
      )}
      {renderEntityGroup(
        filters.include_indirect_reports === true
          ? 'Reports to (direct and indirect)'
          : 'Reports to (direct)',
        filters.reports_to,
      )}

      {renderEntityGroup('Preferred', filters.preferred)}

      {renderEntityGroup('Excluded', filters.excluded)}

      {/* Existing scalar constraints */}

      {filters.tenure && (
        <Chip
          label={`Experience: ${formatTenure()}`}
          title={`Experience: ${formatTenure()}`}
          size="small"
          sx={{
            ...chipStyles,
          }}
        />
      )}

      {filters.performance && (
        <Chip
          label={filters.performance === 'TOP' ? 'Top Tier' : 'Bottom Tier'}
          title={filters.performance === 'TOP' ? 'Top Tier' : 'Bottom Tier'}
          size="small"
          sx={{
            ...chipStyles,
          }}
        />
      )}

      {filters.activity_date && (
        <Chip
          label={`Active: ${
            filters.activity_date.start
              ? moment.utc(filters.activity_date.start).format('DD-MM-YYYY')
              : ''
          }${
            filters.activity_date.end
              ? ` → ${moment.utc(filters.activity_date.end).format('DD-MM-YYYY')}`
              : ''
          }`}
          size="small"
          sx={{
            ...chipStyles,
          }}
        />
      )}
    </Box>
  );
};

const ComparisonRenderer: React.FC<{
  data?: any;
}> = ({ data }) => {
  const { palette } = useTheme<any>();

  const people = data?.people || [];

  if (people.length === 0) {
    return null;
  }

  const columns = data?.columns || [];
  const rankedPeople = [...people].sort(
    (left: any, right: any) =>
      Number(right.matchedEntityCount || 0) - Number(left.matchedEntityCount || 0) ||
      Number(right.focusedEntityScore || 0) - Number(left.focusedEntityScore || 0) ||
      Number(left.performanceRank || 99) - Number(right.performanceRank || 99),
  );
  const preferred = rankedPeople[0];
  const runnerUp = rankedPeople[1];
  const hasSkillFocus = columns.some((column: string) => column.endsWith(' experience'));
  const hasClearPreference =
    preferred &&
    runnerUp &&
    (Number(preferred.matchedEntityCount || 0) !== Number(runnerUp.matchedEntityCount || 0) ||
      Number(preferred.focusedEntityScore || 0) !==
        Number(runnerUp.focusedEntityScore || 0) ||
      preferred.performanceRank !== runnerUp.performanceRank);
  const insight = hasClearPreference
    ? `${preferred.name} has the stronger evidence-led fit${
        hasSkillFocus
          ? `: ${
              preferred.matchedEntityCount
            } focused skills matched with a score of ${Number(
              preferred.focusedEntityScore || 0,
            ).toFixed(2)}`
          : ''
      }. Performance: ${preferred.Performance}; ${runnerUp.name}: ${runnerUp.Performance}.`
    : 'The available focused-skill evidence and performance data do not establish a clear preference.';

  const headerCellStyle: React.CSSProperties = {
    textAlign: 'left',

    padding: '6px',

    overflowWrap: 'anywhere',

    borderBottom: `1px solid ${palette.Divider}`,
  };

  const bodyCellStyle: React.CSSProperties = {
    textAlign: 'left',

    padding: '6px',

    overflowWrap: 'anywhere',

    borderBottom: `1px solid ${alpha(palette.Divider, 0.5)}`,
  };

  return (
    <Box
      sx={{
        mt: 1.5,
        maxWidth: '100%',
        overflowX: 'auto',
      }}
    >
      <Typography
        variant="body4"
        sx={{ display: 'block', mb: 1, color: palette.Text.Paragraph }}
      >
        {insight}
      </Typography>

      <table
        style={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
        }}
      >
        <thead>
          <tr>
            <th style={headerCellStyle}>
              <Typography variant="caption" fontWeight={600}>
                Attribute
              </Typography>
            </th>

            {people.map((person: any) => (
              <th key={person.id} style={headerCellStyle}>
                <Typography variant="caption" fontWeight={600}>
                  {person.name}
                </Typography>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {columns.map((column: string) => (
            <tr key={column}>
              <td style={bodyCellStyle}>
                <Typography variant="caption" fontWeight={600}>
                  {column}
                </Typography>
              </td>

              {people.map((person: any) => (
                <td key={person.id} style={bodyCellStyle}>
                  <Typography variant="caption">{person[column] ?? '—'}</Typography>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

// =============================================================================
// PEOPLE PREVIEW
// =============================================================================

const uniqueDetails = (values: Array<string | null | undefined>): string[] =>
  Array.from(
    new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value)),
  );

const candidateLocation = (candidate: CandidatePreview): string | undefined => {
  const values = uniqueDetails([candidate.city, candidate.country]);
  return values.length ? values.join(', ') : undefined;
};

const hasEvidenceFocusedFilters = (filters: ActiveFilters | undefined): boolean =>
  Boolean(
    filters?.skill?.length ||
      filters?.activity_date ||
      filters?.activity_criticality?.length ||
      filters?.activity_type?.length ||
      filters?.activity_initiative?.length ||
      filters?.activity_source?.length,
  );

const candidateCardDetails = (
  candidate: CandidatePreview,
  filters: ActiveFilters | undefined,
): { subtitle?: string; primary?: string; secondary?: string } => {
  const tenure =
    candidate.tenureYears != null
      ? `${candidate.tenureYears} ${candidate.tenureYears === 1 ? 'year' : 'years'}`
      : undefined;
  const location = candidateLocation(candidate);

  if (hasEvidenceFocusedFilters(filters)) {
    const evidenceCount = candidate.evidenceCount ?? 0;
    const latestEvidenceDate = candidate.latestEvidenceDate
      ? moment(candidate.latestEvidenceDate).format('DD MMM YYYY')
      : undefined;

    return {
      subtitle:
        uniqueDetails([candidate.department, candidate.city, tenure]).join(' \u00b7 ') ||
        undefined,
      primary: `${evidenceCount} ${evidenceCount === 1 ? 'evidence item' : 'evidence items'}`,
      secondary: latestEvidenceDate ? `Last demonstrated: ${latestEvidenceDate}` : undefined,
    };
  }

  const reportsTo = candidate.manager ? `Reports to ${candidate.manager}` : undefined;
  const organization = uniqueDetails([candidate.department, candidate.division]);
  const subtitle =
    (organization.length ? organization : uniqueDetails([reportsTo])).join(' \u00b7 ') ||
    undefined;
  const performanceFocused = Boolean(
    filters?.performance || filters?.performance_ranks?.length,
  );

  if (performanceFocused && candidate.performanceLabel) {
    return {
      subtitle,
      primary: candidate.performanceLabel,
      secondary: uniqueDetails([location, tenure]).join(' \u00b7 ') || undefined,
    };
  }

  return {
    subtitle,
    primary: location || tenure || reportsTo,
    secondary: location ? tenure : subtitle === reportsTo ? undefined : reportsTo,
  };
};

const PeopleMatchPreview = React.memo(
  ({ candidates, filters }: { candidates: CandidatePreview[]; filters?: ActiveFilters }) => {
    const { palette } = useTheme<any>();

    const { getOrgRoute } = useRouter();

    if (!candidates?.length) {
      return null;
    }

    return (
      <Stack
        spacing={1}
        sx={{
          mt: 1.25,
        }}
      >
        {candidates.slice(0, 3).map((candidate) => {
          const details = candidateCardDetails(candidate, filters);

          const hasDetails = !!candidate.topSkills?.length || !!candidate.matchReason;

          return (
            <Card key={candidate.profileId} noElevation>
              <LinkTileWithAvatar
                route={getOrgRoute(AppRoutesEnum.PROFILE, {
                  profileId: candidate.profileId,
                })}
                title={candidate.name}
                subtitle={details.subtitle}
                variant="body4"
                showBorder={false}
                sx={{
                  padding: '0.375rem 0.5rem',
                }}
                endAdornment={
                  details.primary ? (
                    <Stack alignItems="flex-end" sx={{ flexShrink: 0, minWidth: '7.5rem' }}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          color: palette.Icons.Primary.IconFill,
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          maxWidth: '9rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={details.primary}
                      >
                        {details.primary}
                      </Typography>
                      {details.secondary && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: palette.Text.Caption,
                            fontSize: '0.62rem',
                            whiteSpace: 'nowrap',
                            maxWidth: '9rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={details.secondary}
                        >
                          {details.secondary}
                        </Typography>
                      )}
                    </Stack>
                  ) : undefined
                }
              />

              {hasDetails && (
                <Box
                  sx={{
                    px: 1,

                    pb: 1,
                  }}
                >
                  {!!candidate.topSkills?.length && (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {candidate.topSkills.slice(0, 4).map((skill) => (
                        <CXChip key={skill} label={skill} variant="outlined" />
                      ))}
                    </Stack>
                  )}

                  {!!candidate.matchReason && (
                    <EllipsisText
                      variant="body4"
                      lineclamp={2}
                      sx={{
                        mt: 0.5,

                        color: palette.Text.Caption,
                      }}
                    >
                      {candidate.matchReason}
                    </EllipsisText>
                  )}
                </Box>
              )}
            </Card>
          );
        })}
      </Stack>
    );
  },
);

// =============================================================================
// LOADER
// =============================================================================

const ChatResponseLoader = () => {
  const { palette } = useTheme<any>();

  const messages = [
    'Reading the conversation context…',
    'Resolving people and entities…',
    'Building the search…',
    'Searching matching evidence…',
  ];

  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMessageIndex((current) => Math.min(current + 1, messages.length - 1));
    }, 1400);

    return () => window.clearInterval(interval);
  }, [messages.length]);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        height: 24,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[0, 150, 300].map((delay) => (
          <Box
            key={delay}
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: palette.Text.Headline,
              animation: 'bounce 1s infinite',
              animationDelay: `${delay}ms`,
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-4px)' },
              },
            }}
          />
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: palette.Text.Caption }}>
        {messages[messageIndex]}
      </Typography>
    </Box>
  );
};

// =============================================================================
// NAVIGATION
// =============================================================================

const buildNavigationParams = (filters: ActiveFilters): Record<string, string> => {
  const params: Record<string, string> = {};

  const join = (values: string[]) => values.join(',');

  const addFilters = (list: FilterItem[] | undefined, key: string) => {
    if (list?.length) {
      params[key] = join(list.map((item) => item.id));
    }
  };

  const byType = (list: Array<FilterItem & { type?: string }> | undefined, type: string) =>
    (list || []).filter((item) => String(item.type || '').toUpperCase() === type);

  addFilters(filters.skill, 'skill');

  addFilters(byType(filters.location, 'CITY'), 'city');

  addFilters(byType(filters.location, 'COUNTRY'), 'country');

  addFilters(byType(filters.orgUnit, 'DEPARTMENT'), 'department');

  addFilters(byType(filters.orgUnit, 'DIVISION'), 'division');

  addFilters(byType(filters.orgUnit, 'TEAM'), 'team');

  addFilters(byType(filters.orgUnit, 'ORG_PRODUCT'), 'product');

  addFilters(filters.reports_to, 'reportsTo');

  if (filters.reports_to?.length && filters.include_indirect_reports) {
    params.includeIndirectReports = 'true';
  }

  if (filters.focused_people?.length) {
    params.people = join(filters.focused_people.map((person) => person.id));
  }

  if (filters.activity_date) {
    if (filters.activity_date.start) {
      params.activity_date_from = filters.activity_date.start.split('T')[0];
    }

    if (filters.activity_date.end) {
      params.activity_date_to = filters.activity_date.end.split('T')[0];
    }
  }

  if (filters.tenure) {
    if (filters.tenure.min !== undefined) {
      params.min_tenure = String(filters.tenure.min);
    }

    if (filters.tenure.max !== undefined) {
      params.max_tenure = String(filters.tenure.max);
    }
  }

  addFilters(filters.performance_ranks, 'performanceRank');

  return params;
};

const buildActivityNavigationParams = (
  filters: ActiveFilters | undefined,
): Record<string, string> => {
  const params: Record<string, string> = {};
  const addActivityFilters = (items: FilterItem[] | undefined, key: string) => {
    if (items?.length) {
      params[key] = items.map((item) => item.id).join(',');
    }
  };

  const contributorIds = filters?.focused_people?.map((person) => person.id) || [];

  if (contributorIds.length) {
    params.activityContributor = contributorIds.join(',');
  }

  if (filters?.skill?.length) {
    params.activitySkill = filters.skill.map((skill) => skill.id).join(',');
  }

  if (filters?.activity_criticality?.length) {
    params.activityCriticality = filters.activity_criticality
      .map((item) => item.id)
      .join(',');
  }

  addActivityFilters(filters?.activity_type, 'activityType');

  addActivityFilters(filters?.activity_initiative, 'activityInitiative');

  addActivityFilters(filters?.activity_source, 'activitySource');

  if (filters?.activity_date?.start) {
    params.activity_date_from = filters.activity_date.start.split('T')[0];
  }

  if (filters?.activity_date?.end) {
    params.activity_date_to = filters.activity_date.end.split('T')[0];
  }

  return params;
};

// =============================================================================
// SUMMARY
// =============================================================================

const PEOPLE_MATCH_PREVIEW_LIMIT = 3;

const generateHumanReadableSummary = (count: number, filters: ActiveFilters): string => {
  if (count === 0) {
    return 'No matching people found.';
  }

  const parts: string[] = [];

  if (filters.location?.length) {
    parts.push(`in **${filters.location.map((item) => item.name).join(', ')}**`);
  }

  if (filters.orgUnit?.length) {
    parts.push(`within **${filters.orgUnit.map((item) => item.name).join(', ')}**`);
  }

  if (filters.skill?.length) {
    parts.push(`matching **${filters.skill.map((item) => item.name).join(', ')}**`);
  }

  if (filters.tenure) {
    const { min, max } = filters.tenure;

    if (min !== undefined && max !== undefined) {
      parts.push(`with **${min}-${max} years** experience`);
    } else if (min !== undefined) {
      parts.push(`with **${min}+ years** experience`);
    } else if (max !== undefined) {
      parts.push(`with **under ${max} years** experience`);
    }
  }

  if (filters.performance) {
    parts.push(
      filters.performance === 'TOP'
        ? 'with **Top Tier** performance'
        : 'with **Bottom Tier** performance',
    );
  }

  if (filters.activity_date) {
    const { start, end } = filters.activity_date;

    if (start && end) {
      parts.push(
        `active from **${moment.utc(start).format('DD-MM-YYYY')}** to **${moment
          .utc(end)
          .format('DD-MM-YYYY')}**`,
      );
    } else if (start) {
      parts.push(`active since **${moment.utc(start).format('DD-MM-YYYY')}**`);
    } else if (end) {
      parts.push(`active until **${moment.utc(end).format('DD-MM-YYYY')}**`);
    }
  }

  const suffix = parts.length > 0 ? ` ${parts.join(', ')}` : '';

  if (count <= PEOPLE_MATCH_PREVIEW_LIMIT) {
    return `Here are the matching people${suffix}.`;
  }

  return `Here are the top **${PEOPLE_MATCH_PREVIEW_LIMIT}** matches${suffix}.`;
};

// =============================================================================
// VIEW RESULTS BUTTON
// =============================================================================

const ViewResultsButton = React.memo(({ message }: { message: Chat }) => {
  const isUser = message.type === 'user';

  const { goTo } = useRouter();

  const { palette } = useTheme<any>();

  const { account: loggedInUser } = useSession();

  const { intent, active_filters, button_label, data } = message.metadata || {};

  const handleClick = useCallback(() => {
    if (!button_label || !intent) {
      return;
    }

    let pathname: string | undefined;

    switch (intent) {
      case 'PROFILE_OVERVIEW': {
        const profileId = data?.profile?.profileId || data?.id;

        if (!profileId) {
          return;
        }

        pathname = generatePath(
          data?.profile?.skillGroups?.length
            ? AppRoutesEnum.PROFILE_SKILLS
            : AppRoutesEnum.PROFILE_ROOT,
          {
            organizationName: String(loggedInUser.subUrl),

            profileId: String(profileId),
          },
        );

        break;
      }

      case 'LIST_SUMMARY':
      case 'PEOPLE_STATS_VIEW':
        pathname = generatePath(AppRoutesEnum.EXPLORE_PEOPLE_TABLE, {
          organizationName: String(loggedInUser.subUrl),
        });

        break;

      case 'PEOPLE_COMPARISON':
        if (!data?.people?.length) {
          return;
        }

        pathname = generatePath(AppRoutesEnum.EXPLORE_PEOPLE_TABLE, {
          organizationName: String(loggedInUser.subUrl),
        });

        break;

      case 'ACTIVITY_LIST':
        pathname = generatePath(AppRoutesEnum.ACTIVITIES, {
          organizationName: String(loggedInUser.subUrl),
        });

        break;

      default:
        return;
    }

    if (!pathname) {
      return;
    }

    const filterParams = active_filters ? buildNavigationParams(active_filters) : {};

    const comparisonFilterParams = { ...filterParams };
    delete comparisonFilterParams.reportsTo;
    delete comparisonFilterParams.includeIndirectReports;

    const searchParams =
      intent === 'PEOPLE_COMPARISON'
        ? {
            ...comparisonFilterParams,
            people: (data?.people || []).map((person) => person.id).join(','),
            viewType: 'compare',
          }
        : intent === 'LIST_SUMMARY' || intent === 'PEOPLE_STATS_VIEW'
        ? filterParams
        : intent === 'ACTIVITY_LIST'
        ? buildActivityNavigationParams(active_filters)
        : {};

    goTo(
      {
        pathname,

        search: searchParams,
      },
      {},
      {
        state: intent === 'LIST_SUMMARY' || intent === 'PEOPLE_STATS_VIEW' ? {} : undefined,
      },
    );
  }, [active_filters, button_label, data, goTo, intent, loggedInUser.subUrl]);

  if (isUser || !button_label) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={
        <SearchIcon
          sx={{
            fontSize: 14,
          }}
        />
      }
      onClick={handleClick}
      sx={{
        mt: 1,

        textTransform: 'none',

        fontSize: '0.65rem',

        borderRadius: 1,

        py: 0.25,
        px: 1.5,

        height: '28px',

        borderColor: palette.Divider,

        color: palette.Text.Paragraph,

        '&:hover': {
          borderColor: palette.Icons.Primary.IconFill,

          backgroundColor: alpha(palette.Icons.Primary.IconFill, 0.04),
        },
      }}
    >
      {button_label}
    </Button>
  );
});

const MatchExplanationRenderer: React.FC<{
  data?: any;
}> = ({ data }) => {
  const { palette } = useTheme<any>();

  const person = data?.person;

  if (!person) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1.25,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {person.name}
      </Typography>

      {person.summary && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',

            mt: 0.75,

            color: palette.Text.Paragraph,

            lineHeight: 1.5,
          }}
        >
          {person.summary}
        </Typography>
      )}

      {person.required?.length > 0 && (
        <Box
          sx={{
            mt: 1,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            Required matches
          </Typography>

          {person.required.map((item: string) => (
            <Typography key={item} variant="caption" display="block">
              ✓ {item}
            </Typography>
          ))}
        </Box>
      )}

      {person.preferred?.length > 0 && (
        <Box
          sx={{
            mt: 1,
          }}
        >
          <Typography variant="caption" fontWeight={700}>
            Preferred matches
          </Typography>

          {person.preferred.map((item: string) => (
            <Typography key={item} variant="caption" display="block">
              ★ {item}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// =============================================================================
// ANALYTICS RESULT RENDERER
// =============================================================================

/**
 * Renders one analytics cell.
 *
 * Rows carry whole entity objects under dimension names such as `department`
 * or `manager`, so a cell is not always a scalar. Handing one of those objects
 * to React throws and takes the whole conversation down with it, which is why
 * the label is read here rather than assumed.
 */
const formatResultCell = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  if (typeof value === 'boolean' || typeof value === 'string') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(formatResultCell).join(', ');
  }

  const entity = value as Record<string, any>;

  return String(
    entity.displayName ||
      entity.fullName ||
      entity.name ||
      entity.key ||
      JSON.stringify(entity),
  );
};

/**
 * Picks the column carrying the measure.
 *
 * The grouping key comes first and the measures after it, but a query may also
 * select numeric attributes, so the last numeric column is the measure rather
 * than the first one found.
 */
const findMeasureColumn = (result: AnalyticsResultData): string =>
  [...result.columns]
    .reverse()
    .find((column) => typeof result.rows[0][column] === 'number') ||
  result.columns[result.columns.length - 1];

const findLabelColumn = (result: AnalyticsResultData, measureColumn: string): string =>
  result.columns.find((column) => column !== 'profileId' && column !== measureColumn) ||
  result.columns[0];

/**
 * A date histogram labels its buckets with the instant they start, so every
 * yearly bucket reads "1 January" and every monthly bucket reads "the 1st"
 * unless the label is cut back to the granularity the buckets actually have.
 */
const resolveTrendGranularity = (points: moment.Moment[]): string => {
  if (points.every((point) => point.month() === 0 && point.date() === 1)) {
    return 'YYYY';
  }

  if (points.every((point) => point.date() === 1)) {
    return 'MMM YYYY';
  }

  return 'DD MMM YY';
};

/**
 * A time series rendered as bars.
 *
 * A date histogram used to fall through to the raw table, which technically
 * showed the numbers but hid the shape of the series, which is the only reason
 * anyone asks for a trend.
 */
const TrendRenderer: React.FC<{
  result: AnalyticsResultData;
}> = ({ result }) => {
  const { palette } = useTheme<any>();

  const measureColumn = findMeasureColumn(result);

  const labelColumn = findLabelColumn(result, measureColumn);

  const parsed = result.rows.map((row) => {
    const raw = row[labelColumn];

    const instant = moment(
      typeof raw === 'number' ? raw : String(raw),
      moment.ISO_8601,
      false,
    );

    return {
      instant: instant.isValid() ? instant : undefined,

      raw,

      value: Number(row[measureColumn]) || 0,
    };
  });

  const dated = parsed.every((point) => point.instant);

  /**
   * Buckets can arrive newest-first when the question asked for that ordering,
   * which reverses the shape of the series. A trend only reads correctly left
   * to right in time order.
   */
  const points = dated
    ? [...parsed].sort(
        (first, second) => first.instant!.valueOf() - second.instant!.valueOf(),
      )
    : parsed;

  const format = dated
    ? resolveTrendGranularity(points.map((point) => point.instant!))
    : undefined;

  const peak = Math.max(...points.map((point) => point.value), 0);

  return (
    <Box
      sx={{
        mt: 1.5,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-end"
        spacing={0.75}
        sx={{
          height: 140,

          overflowX: 'auto',

          pb: 0.5,
        }}
      >
        {points.map((point, index) => (
          <Stack
            key={`${String(point.raw)}-${index}`}
            alignItems="center"
            justifyContent="flex-end"
            spacing={0.5}
            sx={{
              minWidth: 44,

              flex: 1,

              height: '100%',
            }}
            title={`${
              format ? point.instant!.format(format) : formatResultCell(point.raw)
            }: ${formatResultCell(point.value)}`}
          >
            <Typography variant="caption" fontWeight={600}>
              {formatResultCell(point.value)}
            </Typography>

            <Box
              sx={{
                width: '100%',

                minHeight: 2,

                height: `${peak > 0 ? Math.max(2, (point.value / peak) * 100) : 2}%`,

                borderRadius: 0.5,

                backgroundColor: palette.Icons.Primary.IconFill,
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: palette.Text.Caption,

                whiteSpace: 'nowrap',
              }}
            >
              {format ? point.instant!.format(format) : formatResultCell(point.raw)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const IntentRenderer: React.FC<{
  metadata: NavigationMetadata;
}> = ({ metadata }) => {
  const { intent, data, active_filters, total_count, presentation, unresolvedTerms } =
    metadata;

  const { palette } = useTheme<any>();

  const { getOrgRoute, goTo } = useRouter();

  if (data?.error) {
    return null;
  }

  /**
   * A refused turn. The server did not run a search because part of the
   * question could not be identified, and naming those parts is what lets the
   * user fix the question instead of guessing why the answer looks wrong.
   */
  if (unresolvedTerms?.length) {
    return (
      <Box
        sx={{
          mt: 1,
        }}
      >
        <Typography
          variant="caption"
          display="block"
          sx={{
            color: palette.Text.Caption,
          }}
        >
          Not recognised
        </Typography>

        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
          {unresolvedTerms.map((term) => (
            <Chip key={term} label={term} size="small" variant="outlined" color="warning" />
          ))}
        </Stack>
      </Box>
    );
  }

  if (intent === 'PEOPLE_COMPARISON') {
    return <ComparisonRenderer data={data} />;
  }

  // ---------------------------------------------------------------------------
  // ACTIVITY LIST — summary text + navigation button only, no preview cards
  // ---------------------------------------------------------------------------

  if (intent === 'ACTIVITY_LIST') {
    return null;
  }

  // ---------------------------------------------------------------------------
  // PEOPLE LIST
  // ---------------------------------------------------------------------------

  if (intent === 'LIST_SUMMARY') {
    const summary = generateHumanReadableSummary(total_count || 0, active_filters || {});

    const renderBold = (text: string) =>
      text.split(/(\*\*.*?\*\*)/g).map((part, index) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <Box component="span" key={index} fontWeight={600} color={palette.Text.Headline}>
            {part.slice(2, -2)}
          </Box>
        ) : (
          part
        ),
      );

    return (
      <Box
        sx={{
          mt: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.8125rem',

            lineHeight: 1.5,

            color: palette.Text.Paragraph,
          }}
        >
          {renderBold(summary)}
        </Typography>

        <PeopleMatchPreview candidates={data?.candidates || []} filters={active_filters} />
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // SINGLE PROFILE
  // ---------------------------------------------------------------------------

  if (intent === 'PROFILE_OVERVIEW') {
    const profile = data?.profile;

    if (!profile?.name) {
      return null;
    }

    const attributes: Array<[string, string | null]> = [
      ['Department', profile.department],
      ['Division', profile.division],
      ['Location', profile.location || null],
      ['Tenure', profile.tenureYears != null ? `${profile.tenureYears} yrs` : null],
      ['Manager', profile.manager],
    ];

    return (
      <Card
        noElevation
        sx={{
          mt: 1.25,
        }}
      >
        <LinkTileWithAvatar
          route={getOrgRoute(AppRoutesEnum.PROFILE, {
            profileId: profile.profileId,
          })}
          title={profile.name}
          variant="body3"
          showBorder={false}
          sx={{
            padding: '0.5rem 0.625rem',

            borderBottomLeftRadius: 0,

            borderBottomRightRadius: 0,
          }}
        />

        <CardContent
          sx={{
            pt: 0,

            px: 1.25,

            pb: '1rem !important',
          }}
        >
          <Stack direction="row" flexWrap="wrap" rowGap={1} columnGap={2}>
            {attributes.map(([label, value]) =>
              value ? (
                <Box
                  key={label}
                  sx={{
                    minWidth: 0,
                  }}
                >
                  <EllipsisText
                    variant="body4"
                    lineclamp={1}
                    sx={{
                      color: palette.Text.Caption,
                    }}
                  >
                    {label}
                  </EllipsisText>

                  <EllipsisText variant="body4" fontWeight={500} lineclamp={1}>
                    {value}
                  </EllipsisText>
                </Box>
              ) : null,
            )}
          </Stack>

          {!!profile.skillGroups?.length && (
            <Stack
              spacing={1}
              sx={{
                pt: 1.25,
              }}
            >
              <Typography
                variant="body4"
                component="div"
                fontWeight={600}
                color={palette.Text.Headline}
              >
                {profile.name} has experience in:
              </Typography>

              {profile.skillGroups.map((group) => (
                <Box key={group.label}>
                  <Typography
                    variant="body4"
                    component="div"
                    sx={{
                      color: palette.Text.Caption,
                    }}
                  >
                    {group.label}:{' '}
                    <Box component="span" fontWeight={600} color={palette.Text.Headline}>
                      {group.count}
                    </Box>
                  </Typography>

                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                    {group.skills.map((skill) => (
                      <CXChip key={skill} label={skill} variant="outlined" />
                    ))}

                    {group.count > group.skills.length && (
                      <CXChip
                        label={`+${group.count - group.skills.length} more`}
                        variant="outlined"
                        clickable
                        onClick={() =>
                          goTo(AppRoutesEnum.PROFILE_SKILLS, { profileId: profile.profileId })
                        }
                      />
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}

          {!profile.skillGroups?.length && !!profile.topSkills?.length && (
            <Box
              sx={{
                pt: 1.25,
              }}
            >
              <EllipsisText
                variant="body4"
                lineclamp={1}
                sx={{
                  color: palette.Text.Caption,
                }}
              >
                Most evidenced capabilities
              </EllipsisText>

              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {profile.topSkills.map((skill) => (
                  <CXChip key={skill} label={skill} variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // ANALYTICS
  // ---------------------------------------------------------------------------

  /**
   * The server only sends a person here when it can say which constraints the
   * match rests on. Anything it returned besides that is still worth showing,
   * so the evidence is drawn underneath the explanation by the branch that
   * already knows how, rather than by a second copy of it.
   */
  if (intent === 'MATCH_EXPLANATION' && data?.person) {
    return (
      <>
        <MatchExplanationRenderer data={data} />

        {!!data?.result?.rows?.length && (
          <IntentRenderer metadata={{ ...metadata, intent: 'PEOPLE_STATS_VIEW' }} />
        )}
      </>
    );
  }

  if (intent === 'PEOPLE_STATS_VIEW' || intent === 'MATCH_EXPLANATION') {
    const result = data?.result as AnalyticsResultData | undefined;

    if (!result?.rows?.length) {
      /**
       * An explanation is carried by the message text. Saying "No data found"
       * underneath it would contradict an answer that is already there.
       */
      if (intent === 'MATCH_EXPLANATION') {
        return (
          <PeopleMatchPreview candidates={data?.candidates || []} filters={active_filters} />
        );
      }

      return (
        <Typography
          variant="body2"
          sx={{
            mt: 1,

            color: palette.Text.Caption,

            fontStyle: 'italic',
          }}
        >
          No data found.
        </Typography>
      );
    }

    // KPI ---------------------------------------------------------------------

    if (presentation === 'KPI') {
      const valueColumn = findMeasureColumn(result);

      const value = formatResultCell(result.rows[0][valueColumn]);

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 0.75,
            minWidth: 0,
            py: 0.5,
          }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            color={palette.Icons.Primary.IconFill}
            sx={{ lineHeight: 1 }}
          >
            {value}
          </Typography>

          <Typography variant="body2" sx={{ color: palette.Text.Paragraph, fontWeight: 500 }}>
            matching {value === '1' ? 'person' : 'people'}
          </Typography>
        </Box>
      );
    }

    // RANKED LIST -------------------------------------------------------------

    if (presentation === 'RANKED_LIST') {
      /**
       * A ranking is by a measure, so the value shown has to be the numeric
       * column. Taking the last one blindly showed a manager's name as the
       * rank whenever the query selected an attribute after its measure.
       */
      const valueColumn = findMeasureColumn(result);

      const labelColumn = findLabelColumn(result, valueColumn);

      return (
        <Stack
          spacing={0.75}
          sx={{
            mt: 1.25,
          }}
        >
          {result.rows.map((row, index) => (
            <Stack
              key={`${String(row[labelColumn])}-${index}`}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                py: 0.5,

                borderBottom: `1px solid ${alpha(palette.Divider, 0.4)}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  minWidth: 0,
                }}
              >
                {index + 1}. {formatResultCell(row[labelColumn])}
              </Typography>

              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  ml: 1,
                }}
              >
                {formatResultCell(row[valueColumn])}
              </Typography>
            </Stack>
          ))}
        </Stack>
      );
    }

    // TREND -------------------------------------------------------------------

    if (presentation === 'TREND') {
      return <TrendRenderer result={result} />;
    }

    // TABLE -------------------------------------------------------------------

    /**
     * `profileId` stays in the payload because a follow-up question needs it,
     * but a column of raw identifiers is noise in a table meant to be read.
     */
    const visibleColumns = result.columns.filter((column) => column !== 'profileId');

    return (
      <Box
        sx={{
          mt: 1.5,

          overflowX: 'auto',
        }}
      >
        <table
          style={{
            minWidth: 'max-content',
            borderCollapse: 'collapse',
          }}
        >
          <thead>
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column}
                  style={{
                    textAlign: 'left',

                    padding: '6px',

                    whiteSpace: 'nowrap',

                    borderBottom: `1px solid ${palette.Divider}`,
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {column}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {result.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {visibleColumns.map((column) => (
                  <td
                    key={column}
                    style={{
                      padding: '6px',

                      whiteSpace: 'nowrap',

                      borderBottom: `1px solid ${alpha(palette.Divider, 0.5)}`,
                    }}
                  >
                    <Typography variant="caption">{formatResultCell(row[column])}</Typography>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {result.truncated && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',

              mt: 0.75,

              color: palette.Text.Caption,
            }}
          >
            Results are truncated to the maximum supported dataset.
          </Typography>
        )}
      </Box>
    );
  }

  return null;
};

// =============================================================================
// TAG HIGHLIGHT
// =============================================================================

const HighlightedTagEntityNode = ({ label }: { label: string }) => {
  const { palette } = useTheme();

  return (
    <Box
      component="span"
      sx={{
        color: palette.primary.main,

        fontWeight: 600,

        backgroundColor: `${palette.primary.main}18`,

        borderRadius: '3px',

        px: '4px',
        py: '1px',

        display: 'inline-block',

        mx: '1px',
      }}
    >
      {label}
    </Box>
  );
};

// =============================================================================
// MESSAGE BUBBLE
// =============================================================================

const MessageBubble = React.memo(
  ({
    message,
    onOptionClick,
    taggedEntities,
    canInteract,
    selectedOptionIds,
  }: {
    message: Chat;

    onOptionClick: (input: MessageBubbleInput) => void;

    taggedEntities: TaggedEntity[];

    canInteract: boolean;

    selectedOptionIds: string[];
  }) => {
    const { profile } = useSession();

    const { palette } = useTheme<any>();

    const isUser = message.type === 'user';

    const clarificationSelection = isUser
      ? message.taggedEntities?.find((e) => e.clarificationFor)
      : null;

    const meta: NavigationMetadata = message.metadata || {};

    const hasVisuals = meta.directToClient === true;

    const hasClarification = meta.requiresClarification === true;
    const clarificationOptions = meta.clarificationOptions ?? [];

    const isCompletedKpi = !isUser && hasVisuals && meta.presentation === 'KPI';

    const displayText = (() => {
      const raw = message.response.text;
      if (!clarificationSelection) return raw;
      const sep = raw.indexOf(' — ');
      return sep >= 0 ? raw.slice(0, sep) : raw;
    })();

    const visibleProgressMessages = (meta.progressMessages ?? []).filter(
      (progress) => !/^Found\s+[\d,.]+\s+(?:matching\s+)?people\b/i.test(progress.trim()),
    );

    const renderText = (text: string) => {
      if (!text) {
        return null;
      }

      // Assistant text OR user message without tags.
      if (!isUser || !taggedEntities?.length) {
        const renderInline = (value: string) =>
          value.split(/(\*\*.*?\*\*)/g).map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return (
                <Box component="span" key={index} fontWeight={600}>
                  {part.slice(2, -2)}
                </Box>
              );
            }

            return part;
          });

        const textSx = {
          color: isUser ? palette.Text.Headline : palette.Text.Paragraph,

          fontSize: '0.8125rem',

          lineHeight: 1.5,

          whiteSpace: 'pre-wrap',
        };

        const bulletPattern = /^\s*[-•]\s+/;
        const lines = text.split('\n');

        if (isUser || !lines.some((line) => bulletPattern.test(line))) {
          return (
            <Typography variant="body2" sx={textSx}>
              {renderInline(text)}
            </Typography>
          );
        }

        // Group consecutive "- " lines into a single bulleted list.
        const blocks: Array<
          { type: 'text'; value: string } | { type: 'list'; items: string[] }
        > = [];

        lines.forEach((line) => {
          if (bulletPattern.test(line)) {
            const item = line.replace(bulletPattern, '');
            const last = blocks[blocks.length - 1];

            if (last?.type === 'list') {
              last.items.push(item);
            } else {
              blocks.push({ type: 'list', items: [item] });
            }
          } else if (line.trim()) {
            blocks.push({ type: 'text', value: line });
          }
        });

        return (
          <Box>
            {blocks.map((block, blockIndex) =>
              block.type === 'text' ? (
                <Typography key={blockIndex} variant="body2" sx={textSx}>
                  {renderInline(block.value)}
                </Typography>
              ) : (
                <Box
                  key={blockIndex}
                  component="ul"
                  sx={{
                    my: 0.5,

                    pl: 2.5,
                  }}
                >
                  {block.items.map((item, itemIndex) => (
                    <Typography
                      key={itemIndex}
                      component="li"
                      variant="body2"
                      sx={{
                        ...textSx,

                        mb: 0.25,
                      }}
                    >
                      {renderInline(item)}
                    </Typography>
                  ))}
                </Box>
              ),
            )}
          </Box>
        );
      }

      const sortedTaggedEntities = [...taggedEntities].sort(
        (a, b) => b.label.length - a.label.length,
      );

      if (sortedTaggedEntities.length === 0) {
        return (
          <Typography
            variant="body2"
            sx={{
              color: palette.Text.Headline,

              fontSize: '0.8125rem',

              lineHeight: 1.5,

              whiteSpace: 'pre-wrap',
            }}
          >
            {text}
          </Typography>
        );
      }

      const tagEntityRegex = new RegExp(
        `(${sortedTaggedEntities
          .map((entity) => `@${entity.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)
          .join('|')})`,
        'g',
      );

      const parts = text.split(tagEntityRegex);

      return (
        <Typography
          variant="body2"
          sx={{
            color: palette.Text.Headline,

            fontSize: '0.8125rem',

            lineHeight: 1.5,

            whiteSpace: 'pre-wrap',
          }}
        >
          {parts.map((part, index) => {
            const taggedEntity = taggedEntities.find((entity) => part === `@${entity.label}`);

            if (taggedEntity) {
              return <HighlightedTagEntityNode key={index} label={taggedEntity.label} />;
            }

            return part;
          })}
        </Typography>
      );
    };

    return (
      <Box
        sx={{
          display: 'flex',

          flexDirection: isUser ? 'row-reverse' : 'row',

          gap: 1,

          width: '100%',

          alignItems: 'flex-start',
        }}
      >
        {/* Avatar ---------------------------------------------------------- */}

        <Box
          sx={{
            width: 28,
            height: 28,

            borderRadius: '50%',

            display: 'flex',

            alignItems: 'center',

            justifyContent: 'center',

            flexShrink: 0,

            bgcolor: 'transparent',

            border: isUser ? 'none' : `1px solid ${palette.Divider}`,

            color: isUser ? '#fff' : palette.Icons.Primary.IconFill,
          }}
        >
          {isUser ? (
            <ProfileIcon photo={profile?.photo} isActive={false} />
          ) : (
            <AutoAwesomeIcon
              sx={{
                fontSize: 16,

                color: palette.Text.Headline,
              }}
            />
          )}
        </Box>

        {/* Message --------------------------------------------------------- */}

        <Box
          sx={{
            width: isUser ? 'auto' : 'calc(100% - 36px)',

            maxWidth: isUser ? '85%' : 'none',

            minWidth: 0,

            display: 'flex',

            flexDirection: 'column',

            alignItems: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 1.5,

              maxWidth: '100%',

              overflowWrap: 'anywhere',

              borderRadius: 2,

              backgroundColor: isUser
                ? palette.Buttons.Secondary.BgHover
                : palette.Background,

              border: isUser ? 'none' : `1px solid ${palette.Divider}`,

              borderTopLeftRadius: !isUser ? 0 : 2,

              borderTopRightRadius: isUser ? 0 : 2,

              width: isUser ? 'auto' : '100%',

              minWidth: 'auto',

              boxSizing: 'border-box',
            }}
          >
            {!isUser && visibleProgressMessages.length ? (
              <Stack spacing={0.25} sx={{ mb: 1 }}>
                {visibleProgressMessages.map((progress) => (
                  <Typography
                    key={progress}
                    variant="caption"
                    sx={{ color: palette.Text.Caption, fontSize: '0.68rem' }}
                  >
                    ✓ {progress}
                  </Typography>
                ))}
              </Stack>
            ) : null}

            {!isCompletedKpi && renderText(displayText)}

            {!isUser && meta.accessNote ? (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 1,
                  color: palette.Text.Caption,
                  fontStyle: 'italic',
                  fontSize: '0.7rem',
                  lineHeight: 1.4,
                }}
              >
                {meta.accessNote}
              </Typography>
            ) : null}

            {/* Clarification ----------------------------------------------- */}

            {!isUser && hasClarification && clarificationOptions.length > 0 && (
              <Stack gap={0.5} mt={1}>
                {clarificationOptions.map((option) => {
                  const isSelected = selectedOptionIds.includes(option.id);
                  const selectOption = () => {
                    if (!canInteract) return;
                    onOptionClick({
                      rawUserInput: option.label,
                      taggedEntities: [
                        {
                          id: option.id,
                          label: option.label,
                          entityType: option.entityType || 'PERSON',
                          clarificationFor: message.id,
                        },
                      ],
                    });
                  };

                  const sepIndex = option.label.indexOf(' — ');
                  const optionName =
                    sepIndex >= 0 ? option.label.slice(0, sepIndex) : option.label;
                  const optionSubtitle =
                    sepIndex >= 0 ? option.label.slice(sepIndex + 3) : undefined;

                  return (
                    <Box
                      key={option.id}
                      onClick={canInteract ? selectOption : undefined}
                      sx={{
                        opacity: canInteract || isSelected ? 1 : 0.55,
                        pointerEvents: canInteract ? 'auto' : 'none',
                        cursor: canInteract ? 'pointer' : 'default',
                      }}
                    >
                      <Card
                        noElevation
                        sx={{
                          border: `1px solid ${
                            isSelected ? palette.Icons.Primary.IconFill : palette.Divider
                          }`,
                          bgcolor: isSelected
                            ? alpha(palette.Icons.Primary.IconFill, 0.06)
                            : 'transparent',
                          transition: 'border-color 0.15s, background-color 0.15s',
                          '&:hover': canInteract
                            ? {
                                borderColor: palette.Icons.Primary.IconFill,
                                bgcolor: alpha(palette.Icons.Primary.IconFill, 0.04),
                              }
                            : {},
                        }}
                      >
                        <LinkTileWithAvatar
                          title={optionName}
                          subtitle={optionSubtitle}
                          photo={option.entity?.photo}
                          variant="body4"
                          showBorder={false}
                          readonly
                          sx={{ padding: '0.375rem 0.5rem', pointerEvents: 'none' }}
                        />
                      </Card>
                    </Box>
                  );
                })}
              </Stack>
            )}

            {/* Structured analytics result -------------------------------- */}

            {!isUser && hasVisuals && (
              <Stack>
                {!!meta.ignoredTerms?.length && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ color: palette.Text.Caption }}>
                      I used the criteria I could resolve and skipped:
                    </Typography>

                    <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {meta.ignoredTerms.map((term) => (
                        <Chip
                          key={term}
                          label={term}
                          size="small"
                          variant="outlined"
                          color="warning"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <IntentRenderer metadata={meta} />

                {isFilteredSearchIntent(meta.intent) && (
                  <RenderFilterChips filters={meta.active_filters} />
                )}
              </Stack>
            )}
          </Paper>

          {/* Timestamp ----------------------------------------------------- */}

          <Typography
            variant="caption"
            sx={{
              mt: 0.5,

              fontSize: '0.65rem',

              color: palette.Text.Caption,

              ml: isUser ? 0 : 1,

              mr: isUser ? 1 : 0,
            }}
          >
            {new Date(message.dateTime).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>

          <ViewResultsButton message={message} />
        </Box>
      </Box>
    );
  },
);

// =============================================================================
// CHAT INPUT
// =============================================================================

const ChatInputBox: React.FC<{
  message: string;

  setMessage: React.Dispatch<React.SetStateAction<string>>;

  onSend: (input: MessageBubbleInput) => void;

  taggedEntities: TaggedEntity[];

  setTaggedEntities: React.Dispatch<React.SetStateAction<TaggedEntity[]>>;
  disabled?: boolean;
}> = ({
  message,
  setMessage,
  onSend,
  taggedEntities,
  setTaggedEntities,
  disabled = false,
}) => {
  const { palette, typography } = useTheme<any>();

  const [cursorPos, setCursorPos] = useState(0);

  const [tagQuery, setTagQuery] = useState('');

  const { data: entities = [] } = useSearchableEntities(tagQuery);

  const [anchorElementForPopper, setAnchorElementForPopper] = useState<HTMLElement | null>(
    null,
  );

  const inputFieldReferece = useRef<HTMLTextAreaElement>(null);

  const wrapperReference = useRef<HTMLDivElement>(null);

  const filteredEntities = React.useMemo(() => {
    if (!tagQuery.trim()) {
      return [];
    }

    const lowerCaseInputTerm = tagQuery.toLowerCase();

    const exact: TaggedEntity[] = [];

    const starts: TaggedEntity[] = [];

    const contains: TaggedEntity[] = [];

    for (const entity of entities) {
      if (!entity.label) {
        continue;
      }

      const lowercaseEntityLabel = entity.label.toLowerCase();

      if (lowercaseEntityLabel === lowerCaseInputTerm) {
        exact.push(entity);
      } else if (lowercaseEntityLabel.startsWith(lowerCaseInputTerm)) {
        starts.push(entity);
      } else if (lowercaseEntityLabel.includes(lowerCaseInputTerm)) {
        contains.push(entity);
      }
    }

    return [...exact, ...starts, ...contains];
  }, [tagQuery, entities]);

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;

    const currentCursor = event.target.selectionStart;

    const isDeletion = value.length < message.length;

    if (isDeletion) {
      const deletedIndex = currentCursor;

      const deletedEntity = taggedEntities.find((entity) => {
        const tagEntityText = `@${entity.label}`;

        const mentionStart = message.indexOf(tagEntityText);

        if (mentionStart === -1) {
          return false;
        }

        const mentionEnd = mentionStart + tagEntityText.length;

        return deletedIndex >= mentionStart && deletedIndex < mentionEnd;
      });

      if (deletedEntity) {
        setTaggedEntities((previous) =>
          previous.filter(
            (entity) =>
              !(entity.id === deletedEntity.id && entity.label === deletedEntity.label),
          ),
        );
      }
    }

    setMessage(value);

    const textBeforeCursor = value.slice(0, currentCursor);

    const match = textBeforeCursor.match(/@([\w\s]*)$/);

    if (match) {
      setTagQuery(match[1]);

      setAnchorElementForPopper(wrapperReference.current);

      setCursorPos(currentCursor);
    } else {
      setAnchorElementForPopper(null);
    }
  };

  const handleEntitySelection = (selectedEntity: TaggedEntity) => {
    const textBeforeCursor = message.slice(0, cursorPos);

    const textAfterCursor = message.slice(cursorPos);

    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex === -1) {
      return;
    }

    const newTextBefore = textBeforeCursor.slice(0, lastAtIndex) + `@${selectedEntity.label}`;

    setTaggedEntities((previous) => _.uniqBy([...previous, selectedEntity], 'id'));

    setMessage(newTextBefore + textAfterCursor);

    setTagQuery('');

    setAnchorElementForPopper(null);

    requestAnimationFrame(() => {
      inputFieldReferece.current?.focus();
    });
  };

  const handleSend = () => {
    const trimmedMessage = message.trim();

    if (!trimmedMessage || disabled) {
      return;
    }

    onSend({
      rawUserInput: message,

      taggedEntities: taggedEntities,
    });

    setMessage('');

    setTagQuery('');

    setAnchorElementForPopper(null);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (anchorElementForPopper && event.key === 'Enter') {
      event.preventDefault();

      if (filteredEntities.length > 0) {
        handleEntitySelection(filteredEntities[0]);
      }

      return;
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      handleSend();
    }
  };

  const closePopper = () => {
    setAnchorElementForPopper(null);

    setTagQuery('');
  };

  return (
    <ClickAwayListener onClickAway={closePopper}>
      <Box
        sx={{
          position: 'relative',

          mt: 1,
        }}
        ref={wrapperReference}
      >
        <TextareaAutosize
          ref={inputFieldReferece}
          value={message}
          onChange={handleTextChange}
          onKeyDown={handleKeyPress}
          placeholder="Ask about people or evidenced skills. Type @ to tag a person or term."
          minRows={1}
          maxRows={3}
          disabled={disabled}
          style={{
            width: '100%',

            resize: 'none',

            padding: '8px 36px 8px 10px',

            borderRadius: '8px',

            border: `1px solid ${palette.Divider}`,

            backgroundColor: palette.Background,

            color: palette.Text?.Headline,

            outline: 'none',

            fontFamily: typography?.fontFamily || 'inherit',

            fontSize: '0.8125rem',

            lineHeight: 1.5,
          }}
        />

        <IconButton
          onClick={handleSend}
          disabled={disabled || message.trim() === ''}
          size="small"
          sx={{
            position: 'absolute',

            bottom: 12,
            right: 6,

            color:
              message.trim() === ''
                ? palette.Icons?.Tertiary?.IconFillDefault
                : palette.Icons?.IconFill,
          }}
        >
          <SendIcon
            sx={{
              fontSize: 16,
            }}
          />
        </IconButton>

        <Popper
          open={Boolean(anchorElementForPopper) && filteredEntities.length > 0}
          anchorEl={anchorElementForPopper}
          placement="top-start"
          style={{
            width: wrapperReference.current?.clientWidth,

            zIndex: 1300,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxHeight: 180,

              overflow: 'auto',

              mb: 1,

              ...scrollbarStyles(palette),
            }}
          >
            <List dense disablePadding>
              {filteredEntities.map((entity) => (
                <ListItem
                  key={`${entity.entityType}:${entity.id}`}
                  onClick={() => handleEntitySelection(entity)}
                  sx={{
                    cursor: 'pointer',
                    py: 0.25,
                    minHeight: 34,

                    '&:hover': {
                      backgroundColor: palette.action?.hover,
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontSize="0.78rem">
                        {entity.label}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" fontSize="0.65rem">
                        {entity.entityType}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

// =============================================================================
// CHAT HISTORY
// =============================================================================

export const ChatHistory: React.FC<{
  selectedChatId: string | null;

  onSelect: (id: string) => void;
}> = ({ selectedChatId, onSelect }) => {
  const { data, isLoading, isSuccess } = useGetChatHistory();

  const { palette } = useTheme<any>();

  const [chatHistory, setChatHistory] = useState<
    Array<{
      title: string;
      dateTime: Date;
      id: string;
    }>
  >([]);

  useEffect(() => {
    if (isSuccess && data?.chatHistory) {
      setChatHistory(data.chatHistory);
    }
  }, [isSuccess, data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          p: 2,

          display: 'flex',

          justifyContent: 'center',
        }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Stack
      spacing={0.5}
      sx={{
        height: '100%',

        overflowY: 'auto',

        ...scrollbarStyles(palette),

        pr: 0.5,
      }}
    >
      {chatHistory.length === 0 ? (
        <Typography
          variant="caption"
          sx={{
            color: palette.Text.Caption,

            textAlign: 'center',

            mt: 4,
          }}
        >
          No history.
        </Typography>
      ) : (
        chatHistory.map((item) => {
          const isSelected = item.id === selectedChatId;

          return (
            <Box
              key={item.id}
              onClick={() => onSelect(item.id)}
              sx={{
                borderRadius: 1,

                p: 1,

                cursor: 'pointer',

                bgcolor: isSelected
                  ? alpha(palette.Icons.Primary.IconFill, 0.08)
                  : 'transparent',

                border: '1px solid',

                borderColor: isSelected
                  ? alpha(palette.Icons.Primary.IconFill, 0.2)
                  : 'transparent',

                '&:hover': {
                  bgcolor: alpha(palette.Icons.Primary.IconFill, 0.04),
                },
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontWeight: isSelected ? 600 : 400,

                  fontSize: '0.8rem',
                }}
              >
                {item.title || 'New Search'}
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: palette.Text.Caption,

                  fontSize: '0.7rem',
                }}
              >
                {new Date(item.dateTime).toLocaleDateString()}
              </Typography>
            </Box>
          );
        })
      )}
    </Stack>
  );
};

// =============================================================================
// EMPTY CHAT
// =============================================================================

const SUGGESTIONS = [
  {
    icon: <CodeIcon fontSize="small" />,

    label: 'Find Talent',

    text: 'Find people in Noida with Java evidence and at least 10 years of organizational tenure',
  },

  {
    icon: <SearchIcon fontSize="small" />,

    label: 'Activity',

    text: 'How many people have recent high-criticality activity?',
  },

  {
    icon: <SearchIcon fontSize="small" />,

    label: 'Deep Dive',

    text: 'Find people with recent FIX evidence',
  },
];

export const EmptyChatSuggestions: React.FC<{
  onSelect: (input: MessageBubbleInput) => void;
}> = ({ onSelect }) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        width: '100%',

        height: '100%',

        mt: 4,

        px: 2,
      }}
    >
      <Typography
        variant="caption"
        color={palette.text.secondary}
        sx={{
          mb: 2,

          display: 'block',

          textAlign: 'center',

          textTransform: 'uppercase',

          letterSpacing: 1,
        }}
      >
        Try these searches
      </Typography>

      <Stack spacing={2}>
        {SUGGESTIONS.map((item, index) => (
          <Card
            variant="outlined"
            key={index}
            sx={{
              bgcolor: 'transparent',

              borderColor: palette.Divider,

              borderRadius: 2,

              transition: 'all 0.2s',

              '&:hover': {
                borderColor: palette.primary.main,

                bgcolor: alpha(palette.primary.main, 0.04),

                transform: 'translateY(-2px)',
              },
            }}
          >
            <CardActionArea
              onClick={() =>
                onSelect({
                  rawUserInput: item.text,

                  taggedEntities: [],
                })
              }
              sx={{
                p: 2,

                height: '100%',

                display: 'flex',

                justifyContent: 'flex-start',

                alignItems: 'start',

                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  color: palette.primary.main,

                  mt: 0.5,
                }}
              >
                {item.icon}
              </Box>

              <Box>
                <Typography variant="subtitle2" fontWeight={600} color={palette.text.primary}>
                  {item.label}
                </Typography>

                <Typography
                  variant="body2"
                  color={palette.text.secondary}
                  sx={{
                    lineHeight: 1.3,

                    mt: 0.5,
                  }}
                >
                  "{item.text}"
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

// =============================================================================
// CHAT INTERFACE
// =============================================================================

export const ChatInterface: React.FC<{
  initialChat: Chat[];
  initialChatId: string | null;
}> = ({ initialChat, initialChatId }) => {
  const { palette } = useTheme<any>();

  const [chat, setChat] = useState<Chat[]>(initialChat || []);

  const [chatId, setChatId] = useState<string | null>(initialChatId);

  const [message, setMessage] = useState('');

  const messagesRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: getChatResponse, isLoading } = useGetChatResponse();

  const [taggedEntities, setTaggedEntities] = useState<TaggedEntity[]>([]);

  useEffect(() => {
    setChat(initialChat || []);

    setChatId(initialChatId);

    setMessage('');

    setTaggedEntities([]);
  }, [initialChat, initialChatId]);

  /**
   * Scrolling the container itself rather than the anchor element keeps the
   * page and the drawer where they are; `scrollIntoView` walks every scrollable
   * ancestor and drags them along, horizontally included.
   */
  useEffect(() => {
    const container = messagesRef.current;

    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [chat, isLoading]);

  const handleChatSend = async (messageBubbleInput?: MessageBubbleInput) => {
    const input = messageBubbleInput;

    const currentMessage = input?.rawUserInput || message;

    if (!currentMessage.trim()) {
      return;
    }

    const currentTaggedEntities = input?.taggedEntities ?? taggedEntities;

    setMessage('');

    const optimisticMessage: Chat = {
      type: 'user',

      response: {
        text: currentMessage,
      },

      metadata: {},

      dateTime: new Date(),

      taggedEntities: currentTaggedEntities,
    };

    setChat((previous) => [...previous, optimisticMessage]);

    try {
      const response = (await getChatResponse({
        chatId,

        prompt: currentMessage,

        taggedEntities: currentTaggedEntities,
      })) as {
        data: {
          chatId: string;
          chat: Chat[];
        };
      };

      setTaggedEntities([]);

      setChatId(response.data.chatId);

      setChat(response.data.chat || []);
    } catch (error) {
      console.error(error);

      setChat((previous) => previous.slice(0, -1));

      setMessage(currentMessage);

      setTaggedEntities(currentTaggedEntities);
    }
  };

  const visibleMessages = chat
    .map((chatMessage, originalIndex) => ({ chatMessage, originalIndex }))
    .filter(({ chatMessage }) => {
      const cleanText = (chatMessage.response?.text || '').replace(/^"|"$/g, '').trim();
      const metadata = chatMessage.metadata || {};
      return (
        cleanText.length > 0 ||
        metadata.directToClient === true ||
        metadata.requiresClarification === true
      );
    });
  const latestVisibleMessage = visibleMessages[visibleMessages.length - 1]?.chatMessage;

  return (
    <Stack
      sx={{
        height: '100%',

        minHeight: 0,

        overflow: 'hidden',
      }}
    >
      {/* Messages ------------------------------------------------------------ */}

      <Box
        ref={messagesRef}
        sx={{
          flex: 1,

          minHeight: 0,

          overflowY: 'auto',

          overflowX: 'hidden',

          ...scrollbarStyles(palette),
        }}
      >
        <Container
          maxWidth="md"
          disableGutters
          sx={{
            display: 'flex',

            flexDirection: 'column',

            gap: 2,

            px: 1,

            py: 2,
          }}
        >
          {visibleMessages.map(({ chatMessage: msg, originalIndex }) => {
            const selectedOptionIds = chat
              .slice(originalIndex + 1)
              .filter((item) => item.type === 'user')
              .flatMap((item) => item.taggedEntities || [])
              .filter((entity) => entity.clarificationFor === msg.id)
              .map((entity) => entity.id);
            const canInteract =
              !isLoading &&
              msg === latestVisibleMessage &&
              msg.metadata?.requiresClarification === true &&
              selectedOptionIds.length === 0;

            return (
              <MessageBubble
                key={msg.id || `message-${originalIndex}`}
                message={msg}
                onOptionClick={handleChatSend}
                taggedEntities={msg.taggedEntities || []}
                canInteract={canInteract}
                selectedOptionIds={selectedOptionIds}
              />
            );
          })}

          {isLoading && (
            <Fade in>
              <Box
                sx={{
                  ml: 1,
                }}
              >
                <ChatResponseLoader />
              </Box>
            </Fade>
          )}
        </Container>
      </Box>

      {/* Input --------------------------------------------------------------- */}

      <Box
        sx={{
          flexShrink: 0,
        }}
      >
        <Container maxWidth="md" disableGutters sx={{ px: 1, pb: 1 }}>
          <ChatInputBox
            message={message}
            onSend={handleChatSend}
            setMessage={setMessage}
            taggedEntities={taggedEntities}
            setTaggedEntities={setTaggedEntities}
            disabled={isLoading}
          />
        </Container>
      </Box>
    </Stack>
  );
};
